import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Home, ArrowLeft, Upload, CheckCircle, Circle, Loader2,
  User, Building2, FileText, CreditCard, Briefcase, AlertCircle
} from "lucide-react";
import { API } from "@/App";

const getBuyerAuth = () => {
  const token = localStorage.getItem("buyer_token");
  const applicant = JSON.parse(localStorage.getItem("buyer_applicant") || "{}");
  return { token, applicant };
};

export const BuyerOnboarding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);
  const { token, applicant } = getBuyerAuth();
  
  const [formData, setFormData] = useState({
    // Chain info
    chain_status: "",
    chain_details: "",
    property_to_sell_address: "",
    property_to_sell_status: "",
    // Solicitor
    solicitor_name: "",
    solicitor_email: "",
    solicitor_phone: "",
    solicitor_firm: "",
    // Broker
    broker_name: "",
    broker_email: "",
    broker_phone: ""
  });

  useEffect(() => {
    if (!token) {
      navigate("/buyer/login");
      return;
    }
    fetchOnboardingStatus();
  }, [token]);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API}/sales-automation/onboarding/${applicant.applicant_id}`);
      setOnboardingData(response.data);
      
      // Pre-fill form with existing data
      const app = response.data.applicant || {};
      setFormData(prev => ({
        ...prev,
        chain_status: app.chain_status || "",
        chain_details: app.chain_details || "",
        property_to_sell_address: app.property_to_sell_address || "",
        property_to_sell_status: app.property_to_sell_status || "",
        solicitor_name: app.solicitor_name || "",
        solicitor_email: app.solicitor_email || "",
        solicitor_phone: app.solicitor_phone || "",
        solicitor_firm: app.solicitor_firm || "",
        broker_name: app.broker_name || "",
        broker_email: app.broker_email || "",
        broker_phone: app.broker_phone || ""
      }));
    } catch (error) {
      toast.error("Failed to load onboarding status");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/sales-automation/onboarding/${applicant.applicant_id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Information saved successfully");
      fetchOnboardingStatus();
    } catch (error) {
      toast.error("Failed to save information");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (type, file) => {
    // TODO: Implement actual file upload
    toast.info("File upload will be available soon. Please email documents to your agent.");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const completionPercentage = onboardingData?.completion_percentage || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/buyer/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-slate-800">Complete Your Purchase</h1>
              <p className="text-sm text-slate-500">
                {onboardingData?.deal?.property_address || "Your new home"}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Progress */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Onboarding Progress</span>
              <span className="text-sm text-slate-500">{completionPercentage}% complete</span>
            </div>
            <Progress value={completionPercentage} className="h-2" />
          </CardContent>
        </Card>

        {/* Requirements Checklist */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Required Documents
            </CardTitle>
            <CardDescription>
              Please upload the following documents to proceed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {onboardingData?.requirements?.map((req) => (
                <div
                  key={req.key}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    req.completed ? "bg-green-50 border-green-200" : "bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {req.completed ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-400" />
                    )}
                    <span className={req.completed ? "text-green-700" : "text-slate-700"}>
                      {req.label}
                    </span>
                  </div>
                  {!req.completed && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFileUpload(req.key, null)}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  )}
                  {req.completed && (
                    <Badge className="bg-green-600">Uploaded</Badge>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                For now, please email your documents to <strong>sales@goodacresresidential.co.uk</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Chain Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Your Chain Position
            </CardTitle>
            <CardDescription>
              Tell us about your current property situation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Chain Status</Label>
              <Select value={formData.chain_status} onValueChange={(v) => handleChange("chain_status", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your situation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_chain">No chain - First time buyer or renting</SelectItem>
                  <SelectItem value="related_sale">I have a property to sell</SelectItem>
                  <SelectItem value="related_sale_sstc">I have a property to sell (already SSTC)</SelectItem>
                  <SelectItem value="cash_buyer">Cash buyer - No property to sell</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(formData.chain_status === "related_sale" || formData.chain_status === "related_sale_sstc") && (
              <>
                <div>
                  <Label>Property Address</Label>
                  <Input
                    placeholder="Address of property you're selling"
                    value={formData.property_to_sell_address}
                    onChange={(e) => handleChange("property_to_sell_address", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Sale Status</Label>
                  <Select 
                    value={formData.property_to_sell_status} 
                    onValueChange={(v) => handleChange("property_to_sell_status", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_on_market">Not yet on market</SelectItem>
                      <SelectItem value="on_market">On market</SelectItem>
                      <SelectItem value="under_offer">Under offer</SelectItem>
                      <SelectItem value="sstc">SSTC</SelectItem>
                      <SelectItem value="exchanged">Exchanged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            <div>
              <Label>Additional Chain Information</Label>
              <Textarea
                placeholder="Any other details about your chain..."
                value={formData.chain_details}
                onChange={(e) => handleChange("chain_details", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Solicitor Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Your Solicitor
            </CardTitle>
            <CardDescription>
              Provide your conveyancing solicitor details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Solicitor Name</Label>
                <Input
                  placeholder="John Smith"
                  value={formData.solicitor_name}
                  onChange={(e) => handleChange("solicitor_name", e.target.value)}
                />
              </div>
              <div>
                <Label>Firm Name</Label>
                <Input
                  placeholder="Smith & Co Solicitors"
                  value={formData.solicitor_firm}
                  onChange={(e) => handleChange("solicitor_firm", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="solicitor@example.com"
                  value={formData.solicitor_email}
                  onChange={(e) => handleChange("solicitor_email", e.target.value)}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  placeholder="01234 567890"
                  value={formData.solicitor_phone}
                  onChange={(e) => handleChange("solicitor_phone", e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Broker Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Your Mortgage Broker (Optional)
            </CardTitle>
            <CardDescription>
              If you're using a broker, provide their details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Broker Name</Label>
              <Input
                placeholder="Jane Doe"
                value={formData.broker_name}
                onChange={(e) => handleChange("broker_name", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="broker@example.com"
                  value={formData.broker_email}
                  onChange={(e) => handleChange("broker_email", e.target.value)}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  placeholder="01234 567890"
                  value={formData.broker_phone}
                  onChange={(e) => handleChange("broker_phone", e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate("/buyer/dashboard")}>
            Save & Continue Later
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Save Information
          </Button>
        </div>
      </main>
    </div>
  );
};

export default BuyerOnboarding;
